import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { authApi, teamsApi } from '../utils/api';
import { IconFish } from '../components/Icons';

// ─── LOGIN PAGE ──────────────────────────────────────────────
export function LoginPage() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate(redirectTo || '/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', marginBottom: 4, color: 'var(--text-primary)' }}>
        Welcome Back
      </h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 28, fontSize: '0.9rem' }}>
        Sign in to manage your fishing derbies
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Email</label>
          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" required />
        </div>
        <div className="form-group">
          <label>Password</label>
          <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="••••••••" required />
        </div>
        <div style={{ textAlign: 'right', marginTop: -4, marginBottom: 8 }}>
          <Link to="/forgot-password" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Forgot password?</Link>
        </div>
        <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={loading}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>

      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 10 }}>Don't have an account?</p>
        <Link to={redirectTo ? `/register?redirect=${encodeURIComponent(redirectTo)}` : '/register'} className="btn btn-primary" style={{ width: '100%', display: 'block' }}>Sign Up Free</Link>
      </div>
    </AuthLayout>
  );
}

// ─── REGISTER PAGE ───────────────────────────────────────────
export function RegisterPage() {
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '', role: 'participant' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const redirectTo = searchParams.get('redirect');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(form);

      // Auto-accept team invitation if invite token present
      if (inviteToken) {
        try {
          await teamsApi.acceptInvitation(inviteToken);
          navigate('/teams');
          return;
        } catch (inviteErr) {
          console.error('Auto-accept invitation failed:', inviteErr);
          // Still navigate to dashboard even if invite fails
        }
      }

      navigate(redirectTo || '/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', marginBottom: 4, color: 'var(--text-primary)' }}>
        Create Account
      </h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 28, fontSize: '0.9rem' }}>
        Free to sign up. Pay only when you create an event.
      </p>

      {inviteToken && (
        <div className="alert alert-info" style={{ fontSize: '0.82rem', marginBottom: 16 }}>
          Create an account to accept your team invitation.
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="form-group">
            <label>First Name</label>
            <input type="text" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="John" required />
          </div>
          <div className="form-group">
            <label>Last Name</label>
            <input type="text" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Smith" required />
          </div>
        </div>

        <div className="form-group">
          <label>Email</label>
          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" required />
        </div>

        <div className="form-group">
          <label>Password</label>
          <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" required minLength={8} />
        </div>

        <div className="form-group">
          <label>I am signing up as...</label>
          <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
            <option value="participant">Participant / Angler</option>
            <option value="organizer">Event Organizer</option>
          </select>
        </div>

        {form.role === 'organizer' && (
          <div className="alert alert-info" style={{ fontSize: '0.82rem' }}>
            As an organizer, you can create and manage fishing derbies. You'll be charged per event based on attendee count.
          </div>
        )}

        <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={loading}>
          {loading ? 'Creating account...' : 'Create Account'}
        </button>
      </form>

      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 10 }}>Already have an account?</p>
        <Link to={redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'} className="btn btn-primary" style={{ width: '100%', display: 'block' }}>Sign In</Link>
      </div>
    </AuthLayout>
  );
}

// ─── FORGOT PASSWORD PAGE ────────────────────────────────────
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', marginBottom: 4, color: 'var(--text-primary)' }}>
        Reset Password
      </h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 28, fontSize: '0.9rem' }}>
        Enter your email and we'll send you a reset link
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {sent ? (
        <div className="alert alert-success" style={{ textAlign: 'center' }}>
          If an account exists with that email, we've sent a password reset link. Check your inbox.
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" required />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={loading}>
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
      )}

      <p style={{ textAlign: 'center', marginTop: 24, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        <Link to="/login">Back to Sign In</Link>
      </p>
    </AuthLayout>
  );
}

// ─── RESET PASSWORD PAGE ─────────────────────────────────────
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password.length < 8) {
      return setError('Password must be at least 8 characters');
    }
    if (form.password !== form.confirmPassword) {
      return setError('Passwords do not match');
    }

    setLoading(true);
    try {
      await authApi.resetPassword(token, form.password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout>
        <div className="alert alert-error" style={{ textAlign: 'center' }}>
          Invalid reset link. Please request a new one.
        </div>
        <p style={{ textAlign: 'center', marginTop: 24, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          <Link to="/forgot-password">Request new reset link</Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', marginBottom: 4, color: 'var(--text-primary)' }}>
        Set New Password
      </h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 28, fontSize: '0.9rem' }}>
        Choose a new password for your account
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {success ? (
        <div className="alert alert-success" style={{ textAlign: 'center' }}>
          Password reset successfully! Redirecting to login...
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>New Password</label>
            <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" required minLength={8} />
          </div>
          <div className="form-group">
            <label>Confirm Password</label>
            <input type="password" value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })} placeholder="Re-enter your password" required minLength={8} />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={loading}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      )}

      <p style={{ textAlign: 'center', marginTop: 24, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        <Link to="/login">Back to Sign In</Link>
      </p>
    </AuthLayout>
  );
}

// ─── AUTH LAYOUT WRAPPER ─────────────────────────────────────
function AuthLayout({ children }) {
  const { siteName, logoUrl } = useTheme();
  const { isAuthenticated } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top navbar */}
      <nav className="navbar">
        <div className="navbar-inner">
          <Link to="/" className="navbar-logo">
            {logoUrl ? (
              <img src={logoUrl} alt={siteName} style={{ height: '100%', width: 'auto', objectFit: 'contain' }} />
            ) : (
              <IconFish size="24px" />
            )}
          </Link>

          <div className="navbar-links">
            <Link to="/public/events" className="navbar-link">Events</Link>
            {isAuthenticated ? (
              <Link to="/dashboard" className="btn btn-primary btn-sm">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost btn-sm">Login</Link>
                <Link to="/register" className="btn btn-primary btn-sm">Sign Up</Link>
              </>
            )}
          </div>

          <button
            className="navbar-hamburger"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`navbar-hamburger-icon${menuOpen ? ' open' : ''}`} />
          </button>
        </div>
      </nav>

      {/* Mobile drawer overlay */}
      <div
        className={`navbar-overlay${menuOpen ? ' open' : ''}`}
        onClick={() => setMenuOpen(false)}
      />

      {/* Mobile drawer */}
      <div className={`navbar-drawer${menuOpen ? ' open' : ''}`}>
        <div className="navbar-drawer-links">
          <Link to="/" className="navbar-drawer-link" onClick={() => setMenuOpen(false)}>Home</Link>
          <Link to="/public/events" className="navbar-drawer-link" onClick={() => setMenuOpen(false)}>Events</Link>
          {isAuthenticated ? (
            <Link to="/dashboard" className="navbar-drawer-link" onClick={() => setMenuOpen(false)}>Dashboard</Link>
          ) : (
            <>
              <Link to="/login" className="navbar-drawer-link" onClick={() => setMenuOpen(false)}>Login</Link>
              <Link to="/register" className="navbar-drawer-link" onClick={() => setMenuOpen(false)}>Sign Up</Link>
            </>
          )}
        </div>
      </div>

      {/* Auth card */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, var(--navy-deep) 0%, var(--navy-mid) 50%, var(--navy-deep) 100%)',
        padding: 24,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: '-20%', right: '-10%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, color-mix(in srgb, var(--amber) 6%, transparent) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-15%', left: '-8%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, color-mix(in srgb, var(--blue) 5%, transparent) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div style={{
          width: '100%',
          maxWidth: 440,
          background: 'var(--navy-card)',
          border: '1px solid var(--border)',
          borderRadius: 20,
          padding: '40px 36px',
          position: 'relative',
          zIndex: 1,
          backdropFilter: 'blur(10px)'
        }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            {logoUrl ? (
              <img src={logoUrl} alt={siteName} style={{ height: 48, width: 'auto', objectFit: 'contain' }} />
            ) : (
              <IconFish size="36px" />
            )}
          </div>

          {children}
        </div>
      </div>
    </div>
  );
}
